import React from 'react';
import PropTypes from 'prop-types';
import Moment from 'moment';
import _ from 'lodash';

import Currency from '../Currency';
import { LAYOUT } from '../Layout/enums';
import Tooltip, { InlineTip } from '../Tooltip';

import './OfferDetails.css';

const OfferDetails = ({ offering, layout }) => {
  // First details column
  // Use the parent industry for the industry, when available
  const {
    status,
    industry,
    sector,
    offerType,
    securityType,
    yieldDescription,
    exitDescription,
    recurring,
    categoryName,
    isConditional,
    isComingSoon,
    anticipatedStartDate,
    nonVisibleAppFields,
  } = offering;

  const noShowFields = _.isEmpty(offering.nonVisibleAppFields) ? [] : nonVisibleAppFields.map(field => field.fieldName);
  const closingMmt = Moment.isMoment(offering.closeDate) ? new Moment(offering.closeDate) : offering.closeDate;
  let styleSuff = '';
  switch (layout) {
    case LAYOUT.TAB:
      styleSuff = 'Tab';
      break;
    case LAYOUT.MOB:
      styleSuff = 'Mob';
      break;
    // no default
  }
  const headingStyle = `detailHead${styleSuff}`;
  const detailStyle = `detailStyle${styleSuff}`;
  const itemStyle = `detailItemStyle${styleSuff}`;
  let closingLabel = 'Closed Date';
  if (status.toLowerCase() === 'active') {
    closingLabel = recurring ? 'Next Closing Date' : 'Closing Date';
  }
  // Set the detail to null, if the field should not be displayed
  const details1 = [
    {
      heading: 'Offering Category',
      detail: (
        <div styleName={detailStyle}>
          {categoryName === 'Coming Soon' ?
            <span>
              Testing the Waters
              <Tooltip
                styles={{ display: 'inline-block', marginLeft: 5 }}
                text="Testing the Waters enables companies to test market themselves to gauge potential investor demand to see if it makes sense to move forward with an actual offering.  The SEC created this program so companies can make this test before having to spend the time and cost associated with creating an actual offering."
              />
            </span> :
              categoryName}
        </div>
      ),
      hidden: !isConditional && !isComingSoon,
    },
    {
      heading: 'Anticipated Start Date',
      detail: (
        <div styleName={detailStyle}>
          {anticipatedStartDate}
        </div>
      ),
      hidden: (isConditional || !isComingSoon) || (isComingSoon && (!anticipatedStartDate || anticipatedStartDate.length === 0)),
    },
    {
      heading: 'Offering Type',
      detail: (
        <div styleName={detailStyle}>
          {offerType}
        </div>
      ),
      hidden: (!offerType || offerType.length === 0) && isComingSoon,
    },
    {
      heading: 'Offering Industry',
      detail: (
        <div styleName={detailStyle}>
          {industry}
        </div>
      ),
      hidden: isComingSoon && (!industry || industry.length === 0),
    },
    {
      heading: 'Sector',
      detail: (
        <div styleName={detailStyle}>
          {sector}
        </div>
      ),
      hidden: isComingSoon && (!sector || sector.length === 0),
    },
    {
      heading: closingLabel,
      detail: (
        // eslint-disable-next-line no-nested-ternary
        <div styleName={detailStyle}>{Moment.isMoment(closingMmt) ?
          closingMmt.format('MMMM Do, YYYY') : _.isEmpty(closingMmt) ? '--' : closingMmt}
        </div>
      ),
      hidden: isComingSoon,
    },
    {
      heading: 'Yield',
      detail: <div styleName={detailStyle}>{yieldDescription}</div>,
      hidden: (isComingSoon && (!yieldDescription || yieldDescription.length === 0)) || noShowFields.includes('yieldDescription'),
    },
    {
      heading: 'Exit Strategy/Liquidity',
      /* eslint-disable react/no-danger */
      // exitDescription is controlled by admin. Admin are trusted so
      // is ok to set html directly from data
      detail: (
        <div
          styleName={detailStyle}
          dangerouslySetInnerHTML={{ __html: exitDescription }}
        />
      /* eslint-enable react-no-danger */
      ),
      hidden: isComingSoon && (!exitDescription || exitDescription.length === 0),
    },
  ];
  const details2 = [
    {
      heading: 'Security Type',
      detail: (
        <div styleName={detailStyle}>
          {securityType}
        </div>
      ),
      hidden: isComingSoon && (!securityType || securityType.length === 0),
    },
    {
      heading: 'Offering Price Range',
      detail: (
        <div styleName={`${detailStyle} offerDetailsCurrency`}>
          <Currency amount={offering.minSharePrice} /> - <Currency amount={offering.maxSharePrice} />
        </div>
      ),
      hidden: !isConditional,
    },
    {
      heading: 'Offering Price',
      detail: <Currency
        styleName={detailStyle}
        amount={offering.pricePerShare}
      />,
      tip: <Tooltip text="Per share price at which the issued securities are made available for purchase." />,
      hidden: isConditional || (isComingSoon && (!offering.pricePerShare || offering.pricePerShare.length === 0)),
    },
    {
      heading: 'Offering Amount (Min)',
      detail: Number.isFinite(offering.minTotalRaiseCurrencyAmt) ? <Currency
        styleName={detailStyle}
        amount={offering.minTotalRaiseCurrencyAmt}
        hideCents
      /> : <div styleName={detailStyle}>No offering minimum</div>,
      tip: <Tooltip text="The minimum amount the offer must reach in order to be fulfilled." />,
      hidden: isComingSoon && (!offering.minTotalRaiseCurrencyAmt || offering.minTotalRaiseCurrencyAmt.length === 0),
    },
    {
      heading: 'Offering Amount (Max)',
      detail: Number.isFinite(offering.maxTotalRaiseCurrencyAmt) ? <Currency
        styleName={detailStyle}
        amount={offering.maxTotalRaiseCurrencyAmt}
        hideCents
      /> : <div styleName={detailStyle}>No offering maximum</div>,
      tip: <Tooltip text="The maximum amount an offer will collect before closing." />,
      hidden: isComingSoon && (!offering.maxTotalRaiseCurrencyAmt || offering.maxTotalRaiseCurrencyAmt.length === 0),
    },
    {
      heading: 'Minimum Investment',
      detail: Number.isFinite(offering.minimumInvestCurrencyAmt) ? <Currency
        styleName={detailStyle}
        amount={offering.minimumInvestCurrencyAmt}
        hideCents
      /> : <div styleName={detailStyle}>No minimum investment</div>,
      tip: <Tooltip text="The minimum amount you must invest in the offer in order to qualify." />,
      hidden: isComingSoon && (!offering.minimumInvestCurrencyAmt || offering.minimumInvestCurrencyAmt.length === 0),
    },
    {
      heading: 'Maximum Investment',
      detail: ((ofr) => {
        if (ofr.offerType && ofr.offerType.toLowerCase().replace(/\s+/g, '') === 'rega+') {
          const hasMax = Number.isFinite(ofr.maximumInvestCurrencyAmt);
          const nonAccredTip = "An investor who does not meet the net worth requirements for an accredited investor under the Securities & Exchange Commission's Regulation D. A non-accredited individual investor is one who has a net worth of less than $1 million (including spouse) and who earned less than $200,000 annually ($300,000 with spouse) in the current calendar year.";
          const accredTip = "To be considered an accredited investor, one must have a net worth of at least $1,000,000, excluding the value of one's primary residence, or have income at least $200,000 each year for the last two years (or $300,000 combined income if married) and have the expectation to make the same amount in the current calendar year.";
          return (
            <div>
              {hasMax ? <Currency
                styleName={detailStyle}
                amount={ofr.maximumInvestCurrencyAmt}
                hideCents
              /> : null}
              {hasMax ? <br /> : null}
              <span styleName={detailStyle}>
                <InlineTip text={nonAccredTip}>
                  <span>
                    Non-accredited
                  </span>
                </InlineTip>
                &nbsp;
                <InlineTip text={nonAccredTip}>
                  <span>
                    investors
                  </span>
                </InlineTip> can invest a maximum of
                the greater of 10% of their net worth or 10% of their net income in a
                Reg A+ offering (per offering).
                {!hasMax ? (
                  <span>
                    &nbsp;No maximum limit for&nbsp;
                    <InlineTip text={accredTip}>
                      <span>
                        accredited
                      </span>
                    </InlineTip>
                    &nbsp;
                    <InlineTip text={accredTip}>
                      <span>
                        investors.
                      </span>
                    </InlineTip>
                  </span>
                ) : null}
              </span>
            </div>);
        }
        return (Number.isFinite(ofr.maximumInvestCurrencyAmt) ? <Currency
          styleName={detailStyle}
          amount={ofr.maximumInvestCurrencyAmt}
          hideCents
        /> : <div styleName={detailStyle}>No maximum investment</div>);
      })(offering),
      tip: <Tooltip text="The maximum amount you are allowed to invest in an offer." />,
      hidden: isComingSoon && (!offering.maximumInvestCurrencyAmt || offering.maximumInvestCurrencyAmt.length === 0),
    },
  ];
  return (
    <div styleName="detailsView">
      <div styleName="detailsColumn">
        {details1.map(det => (
          <div key={det.heading}>
            {
              !det.hidden ?
                <div styleName={itemStyle} key={det.heading}>
                  <div styleName="detailHeadRow">
                    <div styleName={headingStyle}>{det.heading}</div>
                    {det.tip ? det.tip : null}
                  </div>
                  {det.detail}
                </div> : null
            }
          </div>
        ))}
      </div>
      <div styleName="detailsColumn">
        {details2.map(det => (
          <div>
            {
              !det.hidden ?
                <div styleName={itemStyle} key={det.heading}>
                  <div styleName="detailHeadRow">
                    <div styleName={headingStyle}>{det.heading}</div>
                    {det.tip ? det.tip : null}
                  </div>
                  {det.detail}
                </div> : null
            }
          </div>
        ))}
      </div>
    </div>);
};

OfferDetails.propTypes = {
  offering: PropTypes.shape({
    industry: PropTypes.string.isRequired,
    closeDate: PropTypes.string,
    securityType: PropTypes.string.isRequired,
    offerType: PropTypes.string.isRequired,
    exitDescription: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
};

export default OfferDetails;



// WEBPACK FOOTER //
// ./src/components/Offering/OfferDetails.js